import axios from "../config/axios";
import type { AxiosError, AxiosRequestConfig } from "axios";

import { ApiConversation, ApiMessage, SharedConversationResponse } from "../shared/interfaces";
import type { ProgressLine } from "../hooks/streamReducer";
import {
    comparatorConversationOldestToNewestUpdate,
    comparatorMessageOldToNew,
    explicitUtc,
} from "../util/objectUtil";
import { hasNoConversations } from "../hooks/configuration";
import { readConversation } from "../marvin/partReader";

/** A timestamp as the wire carries it (a string, whatever the declared type) with its zone stated. */
function statedUtc<T>(value: T): T {
    return (typeof value === "string" ? explicitUtc(value) : value) as T;
}

function decodeMessage(message: ApiMessage): ApiMessage {
    if (typeof message !== "object" || message === null) return message;
    return {
        ...message,
        createdAt: statedUtc(message.createdAt),
        ...(message.response ? { response: decodeMessage(message.response) } : {}),
    };
}

/**
 * Every conversation read decodes here, once: its parts (`readConversation`) and its
 * timestamps, which the API writes in UTC but not always with a zone. Downstream, a
 * zoneless timestamp read by `new Date` would be local time: the Questions list said
 * "in 6h" for a question asked minutes ago.
 */
function decodeConversation(conversation: ApiConversation): ApiConversation {
    const read = readConversation(conversation);
    if (typeof read !== "object" || read === null || !Array.isArray(read.messages)) return read;
    return {
        ...read,
        createdAt: statedUtc(read.createdAt),
        updatedAt: statedUtc(read.updatedAt),
        messages: read.messages.map(decodeMessage),
    };
}

export interface ConversationApiTransport {
    get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<{ data: T }>;
    post<T = unknown>(
        url: string,
        body?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<{ data: T }>;
    patch<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
}

export interface ConversationApiFactoryOptions {
    hasNoConversations?: () => boolean;
}

/** Opaque page token minted by the API; never parsed or constructed here. */
export type ConversationHistoryCursor = string;

interface ConversationHistoryResponse {
    conversations: ApiConversation[];
    next_cursor: ConversationHistoryCursor | null;
}

export interface ConversationHistoryPage {
    items: ApiConversation[];
    nextCursor: ConversationHistoryCursor | null;
    /** The legacy API supplies recent questions only; this is not complete history. */
    recentOnly?: boolean;
}

export interface ConversationApiClient {
    getConversation(id: string): Promise<ApiConversation>;
    /** The finished turn `turnMessageId` belongs to, plus anything written after it, with
     *  the thread's conversation-level fields. An older server returns the whole thread
     *  (without `turnMessageId`), so callers must check the echo before merging. */
    getConversationTurn(id: string, turnMessageId: string): Promise<ApiConversation>;
    getAllConversations(): Promise<ApiConversation[]>;
    getConversationPage(
        pageSize: number,
        cursor?: ConversationHistoryCursor,
    ): Promise<ConversationHistoryPage>;
    createConversation(
        message: string,
        messageType: string,
        documentFilters?: string[],
        agent?: string,
    ): Promise<ApiConversation>;
    saveMessageTrace(messageId: string, trace: ProgressLine[]): Promise<void>;
    shareConversation(conversationId: string, reuseExisting?: boolean): Promise<string>;
    getSharedConversation(shareId: string): Promise<SharedConversationResponse>;
    continueSharedConversation(shareId: string): Promise<string>;
    exportPdf(conversationId: string): Promise<Blob>;
    updateDocumentFilters(conversationId: string, documentFilters: string[]): Promise<void>;
}

/**
 * Creates the conversation client against an injected transport. The web and
 * iOS singleton below keeps the existing axios behavior. Native desktop can
 * provide an origin-pinned authenticated transport without importing axios.
 */
export function createConversationApi(
    transport: ConversationApiTransport,
    options: ConversationApiFactoryOptions = {},
): ConversationApiClient {
    const conversationListDisabled = options.hasNoConversations ?? hasNoConversations;

    return {
        getConversationPage: async (pageSize, cursor) => {
            if (conversationListDisabled()) return { items: [], nextCursor: null };
            let data: ConversationHistoryResponse;
            try {
                // The dedicated route prevents old replicas from silently ignoring a cursor.
                ({ data } = await transport.get<ConversationHistoryResponse>(
                    "/api/ai/conversation/history",
                    { params: { limit: pageSize, ...(cursor ? { cursor } : {}) } },
                ));
            } catch (error) {
                const status = (error as AxiosError | undefined)?.response?.status;
                // Keep recent questions usable before the paged API rolls out. Never
                // substitute the first page for a failed cursor, auth, or server request.
                if (cursor || (status !== 404 && status !== 405)) throw error;
                const recent = await transport.get<ApiConversation[]>("/api/ai/conversation");
                return {
                    items: recent.data
                        .map(decodeConversation)
                        .sort(
                            (a, b) =>
                                new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                        )
                        .slice(0, pageSize),
                    nextCursor: null,
                    recentOnly: true,
                };
            }
            // The server decides where the page ends and what the next one starts
            // from; the client never reconstructs the sort key.
            return {
                items: data.conversations.map(decodeConversation),
                nextCursor: data.next_cursor,
            };
        },

        getConversation: async (id: string): Promise<ApiConversation> => {
            const resp = await transport.get<ApiConversation>(`/api/ai/conversation/${id}`);
            return decodeConversation(resp.data);
        },

        getConversationTurn: async (id, turnMessageId) => {
            const resp = await transport.get<ApiConversation>(`/api/ai/conversation/${id}`, {
                params: { turn_message_id: turnMessageId },
            });
            return decodeConversation(resp.data);
        },

        getAllConversations: async (): Promise<ApiConversation[]> => {
            if (conversationListDisabled()) {
                return [];
            }

            const resp = await transport.get<ApiConversation[]>("/api/ai/conversation");
            const conversations = resp.data
                .map(decodeConversation)
                .sort(comparatorConversationOldestToNewestUpdate);
            conversations.forEach((conversation) =>
                conversation.messages.sort(comparatorMessageOldToNew),
            );
            return conversations;
        },

        createConversation: async (
            message: string,
            messageType: string,
            documentFilters?: string[],
            /** Tags the conversation for a specialized agent (e.g. "admin_edit").
             * Admin-gated server-side (403 otherwise); agent-tagged conversations
             * are hidden from the normal conversation list. */
            agent?: string,
        ): Promise<ApiConversation> => {
            const resp = await transport.post<ApiConversation>("/api/ai/conversation", {
                message,
                messageType,
                documentFilters,
                ...(agent ? { agent } : {}),
            });
            return decodeConversation(resp.data);
        },

        // Persist the client-built activity trace so a reopened chat reproduces the live
        // streaming view. Display-only and best-effort — callers fire-and-forget.
        saveMessageTrace: async (messageId: string, trace: ProgressLine[]): Promise<void> => {
            await transport.post(`/api/ai/conversation/message/${messageId}/trace`, { trace });
        },

        // Freeze the conversation into a read-only snapshot and return its share id. Only
        // the owner may share (the backend 404s otherwise). Feedback requests reuse a live
        // immutable snapshot; explicit Share actions still mint a current snapshot.
        shareConversation: async (
            conversationId: string,
            reuseExisting: boolean = false,
        ): Promise<string> => {
            const url = `/api/ai/conversation/${conversationId}/share`;
            const resp = reuseExisting
                ? await transport.post<{ shareId: string }>(url, { reuseExisting: true })
                : await transport.post<{ shareId: string }>(url);
            return resp.data.shareId;
        },

        // Fetch a shared snapshot by its share id (messages already filtered server-side).
        getSharedConversation: async (shareId: string): Promise<SharedConversationResponse> => {
            const resp = await transport.get<SharedConversationResponse>(
                `/api/ai/conversation/shared/${shareId}`,
            );
            return { ...resp.data, conversation: decodeConversation(resp.data.conversation) };
        },

        // Copy a share's answered questions into a new conversation of the caller's, to ask
        // follow-ups in; returns that conversation's id. The shared one is unchanged.
        continueSharedConversation: async (shareId: string): Promise<string> => {
            const resp = await transport.post<{ conversationId: string }>(
                `/api/ai/conversation/shared/${shareId}/continue`,
            );
            return resp.data.conversationId;
        },

        // Branded PDF of the conversation. Fetched through the transport rather than linked
        // as a raw href so native Bearer auth and the configured API base both apply.
        exportPdf: async (conversationId: string): Promise<Blob> => {
            const resp = await transport.get<Blob>(
                `/api/ai/conversation/${conversationId}/export.pdf`,
                {
                    responseType: "blob",
                    headers: { Accept: "application/pdf" },
                },
            );
            return resp.data;
        },

        // Persist a new document scope for the conversation — [] clears it back to
        // unscoped. Callers only need success + a conversation-query invalidation.
        updateDocumentFilters: async (
            conversationId: string,
            documentFilters: string[],
        ): Promise<void> => {
            await transport.patch(`/api/ai/conversation/${conversationId}`, {
                documentFilters,
            });
        },
    };
}

export const ConversationApi = createConversationApi(axios);
